import { LegacyPortalSearchServiceSearchEventResponse } from "@spwn-portal/grpc";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";

import { managePageNumber } from "../managePageNumber";
import { useSearchedCondition } from "../useSearchedCondition";
import { convertResponse } from "./functions/convertResponse";

import type { EventSearchCondition } from "../../types/eventSearchCondition";
import type { PromiseClient } from "@bufbuild/connect";
import type { LegacyPortalSearchService } from "@spwn-portal/grpc";
import type { HostingType } from "@spwn/types/firebase/firestore";
import type { MutationFetcher } from "swr/mutation";

type Input = {
  belongedHosting: HostingType;
  client: PromiseClient<typeof LegacyPortalSearchService>;
};

const SWR_KEY = "searchEvent";
const { getPage, incrementPage, resetPage } = managePageNumber();

export const useEventSearch = (input: Input) => {
  const { belongedHosting, client } = input;
  const [hasEventSearched, searchedCondition, setSearchedCondition] =
    useSearchedCondition<"event">();
  const fetcher: MutationFetcher<
    LegacyPortalSearchServiceSearchEventResponse,
    string,
    EventSearchCondition
  > = async (_, { arg }) => {
    const { pageNumber, ...rest } = arg;
    const searchCondition = {
      pageNumber,
      ...rest,
      hostingType: belongedHosting,
    } as const;
    const result = client.searchEvent(searchCondition);
    setSearchedCondition({ ...searchCondition, type: "event" });
    return result;
  };
  const { data } = useSWR<LegacyPortalSearchServiceSearchEventResponse>(
    SWR_KEY,
    null,
    { revalidateOnMount: false }
  );
  const { isMutating, trigger: eventTrigger } = useSWRMutation(
    SWR_KEY,
    fetcher
  );
  const eventResultCount = data?.totalCount ?? 0;
  const eventSearchResults = data?.items.map(convertResponse) ?? [];

  const handleSearchEvent = (input: EventSearchCondition) => {
    resetPage();
    // 非同期関数じゃない。await すると実行時に runtime error が出る
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    eventTrigger(input, {
      populateCache: true,
    });
  };
  const handleLoadMoreEvents = () => {
    if (searchedCondition === null) return;
    incrementPage();
    const { type: _, ...condition } = searchedCondition;

    // 非同期関数じゃない。await すると実行時に runtime error が出る
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    eventTrigger(
      {
        ...condition,
        pageNumber: getPage(),
      },
      {
        populateCache: (newData, currentData) => {
          const base =
            currentData?.clone() ??
            new LegacyPortalSearchServiceSearchEventResponse({
              items: [],
              totalCount: 0,
            });
          base.items.push(...newData.items);
          base.totalCount = newData.totalCount ?? base.totalCount;
          return base;
        },
        revalidate: false,
        rollbackOnError: true,
      }
    );
  };

  return {
    eventResultCount,
    eventSearchResults,
    eventTrigger,
    handleLoadMoreEvents,
    handleSearchEvent,
    hasEventSearched,
    isLoading: isMutating && data === undefined,
    isMoreLoading: isMutating,
  };
};
